import { useEffect, useSyncExternalStore } from 'react';

/**
 * React hook for fetching and mutating the authenticated buyer's saved
 * searches. Reads through the product-shaped `customerAccount.buying.savedSearches`
 * API; customer identity is resolved server-side from the session cookie.
 *
 * Cosmos uses `graphql-request` via the generated SDK exposed by
 * `getAthenaClient()`, so this hook composes a small module-level store +
 * `useSyncExternalStore` instead of Apollo `useQuery`/`useMutation`. The store
 * is shared by every consumer (the switcher dropdown, the save button, the
 * rename/delete dialogs), so a create/update/delete from any surface refetches
 * once and all mounted consumers re-render — no prop-drilling or context, and
 * no stale per-instance copies that only refresh on reload.
 */
import { useAuth } from '@opendoor/auth-fe/context';
import type { SavedSearchFieldsFragment, SavedSearchInput } from '__generated__/athena';
import { captureError } from 'helpers/captureError';

import { getAthenaClient } from '../../api';

export type SavedSearchMutationResult = {
  success: boolean;
  errorMessage?: string | null;
  savedSearch?: SavedSearchFieldsFragment | null;
};

export type RemoveSavedSearchResult = {
  success: boolean;
  errorMessage?: string | null;
};

export interface UseSavedSearchesApi {
  isLoggedIn: boolean;
  loading: boolean;
  savedSearches: SavedSearchFieldsFragment[];
  add: (input: SavedSearchInput) => Promise<SavedSearchMutationResult>;
  update: (id: string, input: SavedSearchInput) => Promise<SavedSearchMutationResult>;
  remove: (id: string) => Promise<RemoveSavedSearchResult>;
  refetch: () => Promise<void>;
}

// The Athena resolver rejects saved searches without any location (market,
// zone, or polygon) with `success: false`. Catch that here so the hook
// surfaces a uniform client-side error without paying for a round-trip.
const inputHasLocation = (input: SavedSearchInput): boolean =>
  !!input.location.market ||
  (input.location.zoneIds?.length ?? 0) > 0 ||
  (input.location.vertices?.length ?? 0) > 0;

const LOCATION_REQUIRED_ERROR = 'A location is required to save a search.';

// ---- Shared module-level store ----
// Saved searches are read and mutated from several surfaces at once. A single
// module store keeps them in sync via `useSyncExternalStore`: mutations refetch
// once and every subscribed consumer re-renders.

interface SavedSearchesStoreState {
  savedSearches: SavedSearchFieldsFragment[];
  loading: boolean;
}

let storeState: SavedSearchesStoreState = { savedSearches: [], loading: false };
const listeners = new Set<() => void>();
// Latest in-flight request, so a stale response from a previous auth state
// (e.g. logout-mid-fetch) cannot overwrite fresher state.
let requestId = 0;
// Latest auth state, so a mutation that resolves after logout doesn't refetch
// against an unauthenticated session.
let loggedIn = false;

const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const getSnapshot = (): SavedSearchesStoreState => storeState;

const setStoreState = (next: Partial<SavedSearchesStoreState>) => {
  storeState = { ...storeState, ...next };
  listeners.forEach((listener) => listener());
};

const fetchSavedSearches = async (): Promise<void> => {
  const id = ++requestId;
  setStoreState({ loading: true });
  try {
    const data = await getAthenaClient().GetSavedSearches();
    if (id !== requestId) {
      return;
    }
    setStoreState({ savedSearches: data?.customerAccount?.buying?.savedSearches ?? [] });
  } catch (e) {
    captureError(e, 'GetSavedSearches');
    if (id !== requestId) {
      return;
    }
    setStoreState({ savedSearches: [] });
  } finally {
    if (id === requestId) {
      setStoreState({ loading: false });
    }
  }
};

const clearSavedSearches = () => {
  requestId++;
  setStoreState({ savedSearches: [], loading: false });
};

const addSavedSearch = async (input: SavedSearchInput): Promise<SavedSearchMutationResult> => {
  if (!inputHasLocation(input)) {
    return { success: false, errorMessage: LOCATION_REQUIRED_ERROR, savedSearch: null };
  }
  try {
    const res = await getAthenaClient().AddSavedSearch({ input });
    const payload = res?.customerAccount?.buying?.savedSearches?.add;
    if (payload?.success && loggedIn) {
      await fetchSavedSearches();
    }
    return {
      success: payload?.success ?? false,
      errorMessage: payload?.errorMessage ?? null,
      savedSearch: payload?.savedSearch ?? null,
    };
  } catch (e) {
    captureError(e, 'AddSavedSearch');
    return { success: false, errorMessage: 'Unexpected error', savedSearch: null };
  }
};

const updateSavedSearch = async (
  id: string,
  input: SavedSearchInput,
): Promise<SavedSearchMutationResult> => {
  if (!inputHasLocation(input)) {
    return { success: false, errorMessage: LOCATION_REQUIRED_ERROR, savedSearch: null };
  }
  try {
    const res = await getAthenaClient().UpdateSavedSearch({ id, input });
    const payload = res?.customerAccount?.buying?.savedSearches?.update;
    if (payload?.success && loggedIn) {
      await fetchSavedSearches();
    }
    return {
      success: payload?.success ?? false,
      errorMessage: payload?.errorMessage ?? null,
      savedSearch: payload?.savedSearch ?? null,
    };
  } catch (e) {
    captureError(e, 'UpdateSavedSearch');
    return { success: false, errorMessage: 'Unexpected error', savedSearch: null };
  }
};

const removeSavedSearch = async (id: string): Promise<RemoveSavedSearchResult> => {
  try {
    const res = await getAthenaClient().RemoveSavedSearch({ id });
    const payload = res?.customerAccount?.buying?.savedSearches?.remove;
    if (payload?.success && loggedIn) {
      await fetchSavedSearches();
    }
    return {
      success: payload?.success ?? false,
      errorMessage: payload?.errorMessage ?? null,
    };
  } catch (e) {
    captureError(e, 'RemoveSavedSearch');
    return { success: false, errorMessage: 'Unexpected error' };
  }
};

/**
 * Resets the module-level store. Exported for tests (and HMR) to isolate state
 * between runs; not used by application code.
 */
export const __resetSavedSearchesStoreForTest = () => {
  storeState = { savedSearches: [], loading: false };
  requestId += 1;
  loggedIn = false;
};

export function useSavedSearches(): UseSavedSearchesApi {
  const { authentication } = useAuth();
  const authState = authentication?.state;
  const isLoggedIn = authState === 'authenticated';
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  useEffect(() => {
    loggedIn = isLoggedIn;
    if (isLoggedIn) {
      void fetchSavedSearches();
      return;
    }
    // `authenticating` is the transient state emitted on initial load and
    // session refresh. Clearing the list (and flipping the dropdown's
    // visibility guard) during it would let a background re-render tear down
    // an open saved-searches menu, so we keep the existing list and only reset
    // on a definitively logged-out state.
    if (authState === 'authenticating') {
      return;
    }
    clearSavedSearches();
  }, [isLoggedIn, authState]);

  return {
    isLoggedIn,
    loading: snapshot.loading,
    savedSearches: snapshot.savedSearches,
    add: addSavedSearch,
    update: updateSavedSearch,
    remove: removeSavedSearch,
    refetch: fetchSavedSearches,
  };
}
